import { useState, useRef, KeyboardEvent } from 'react';
import { Button } from '../../../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import Logo from '../../../components/Logo/Logo';
import { registrationService } from '../../../services/api/registrationService';
import { useAppSelector } from "../../../store/index";
import { setToken } from "../../../redux/slices/userProfileSlice";
import { useDispatch } from "react-redux";

interface OtpVerificationProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function OtpVerification({ onContinue, onBack }: OtpVerificationProps) {
  const email = useAppSelector(
    (state) => state.userProfile.email
  );
  const dispatch = useDispatch();
  
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
const [successMessage, setSuccessMessage] = useState<string | null>(null);
const [error, setError] = useState<string | null>(null);

  const handleChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newOtp[index] = char;
      }
    });
    setOtp(newOtp);

    const nextEmptyIndex = newOtp.findIndex(val => !val);
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  const isButtonDisabled = otp.some(digit => !digit);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (isButtonDisabled) return;

  setError(null);
  setSuccessMessage(null);

  try {
    const response = await registrationService.verifyForgotPasswordOtp(
      email,
      otp.join('')
    );

    if (response.success) {
      setSuccessMessage('OTP verified successfully!');

      dispatch(setToken(response.data?.passwordToken || ''));

      setTimeout(() => {
        onContinue(); 
      }, 1500);

      return;
    }

    const errorMessage =
      (Array.isArray(response.errors) && response.errors[0]) ||
      response.message ||
      'Invalid OTP';

    setError(errorMessage);
  } catch (error: any) {
    const data = error?.response?.data;

    const errorMessage =
      (Array.isArray(data?.errors) && data.errors[0]) ||
      data?.message ||
      'Invalid or expired OTP';

    setError(errorMessage);
  }
};


  const handleResend = async () => {
    setError("Not implemented yet.");    
  };
  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col items-center px-6 pt-16 pb-8">
      <div className="w-full max-w-sm mb-4">
        <button 
          onClick={onBack}
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6 text-brand-primary" />
        </button>
      </div>

      <div className="w-full max-w-sm flex flex-col items-center -translate-y-[10%]">
        <div className="mb-12">
          <Logo />
        </div>

        <h1 className="text-center mb-10">Enter Verification Code</h1>
        {error && (
          <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="w-full mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600 text-center">{successMessage}</p>
          </div>
        )}
        <p className="text-center text-[#757575] mb-8">
          We've sent a 6-digit code to your email
        </p>

        <div className="flex gap-2 sm:gap-3 justify-center mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-12 h-12 sm:w-14 sm:h-14 text-center rounded-lg border-2 transition-all focus:outline-none ${
                digit
                  ? 'border-brand-primary bg-gradient-to-r from-brand-primary/10 to-brand-gray/10'
                  : 'border-gray-300 bg-white'
              } focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20`}
            />
          ))}
        </div>

        <button
        onClick={handleResend}
          type="button"
          className="text-[#757575] hover:text-brand-primary text-sm transition-colors mt-4"
        >
          Didn't receive code? Resend
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="w-full max-w-sm flex flex-col flex-1">
        <div className="mt-auto pt-10 pb-4 w-full">
          <Button 
            type="submit"
            disabled={isButtonDisabled}
            className="w-full bg-brand-primary hover:bg-brand-hover text-white h-12 sm:h-14 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed text-center"
          >
            Verify
          </Button>
        </div>
      </form>
    </div>
  );
}
